import {
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AccountInput, ExtensionTypes, QueueManagementStrategy } from 'generated/graphql-types';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import { NavigatableDetailView } from 'src/app/common-components/detail/detail-view';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { QueueManagementStrategyOption } from 'src/app/common-components/entity-select/queue-management/models/queue-management-option';
import { LcsSingleSelectSelectedEvent } from 'src/app/common-components/entity-select/single-select/single-select-base';
import { getLogLevelPrivileges } from 'src/app/privileges/privileges.selector';
import { createPromptSuccessNoRouteToDetail } from '../../add-prompt/state/add-prompt.actions';
import { ExtensionPrivileges } from '../../extension-details/models/extension-privileges';
import { AccountActiveDatesComponent } from '../account-active-dates/containers/account-active-dates.component';
import { AccountLogLevelComponent } from '../account-log-level/containers/account-log-level.component';
import { AdvancedAccountSettingsComponent } from '../advanced-account-settings/containers/advanced-account-settings.component';
import { AccountDetailSaveEvent } from '../models/account-detail-form-submit-event';
import { AccountPrivileges } from '../models/account-privileges';
import { GetAccountDetail_account } from '../queries/generated/GetAccountDetail';
import { AccountDetailFormService } from '../services/account-detail-form.service';
import {
   addMohPrompt,
   bulkConfigureExtensions,
   editPresenceCredentials,
   rebootAllPhones,
} from '../state/account-detail.actions';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-account-detail-view',
   styleUrls: ['./account-detail-view.component.scss'],
   templateUrl: './account-detail-view.component.html',
   standalone: false,
})
export class AccountDetailViewComponent extends NavigatableDetailView implements OnInit, OnDestroy {
   @Input() callState: CallState;

   private _originalHoldMusicPrompt: GetAccountDetail_account;

   private destroyed$ = new Subject<void>();

   get entityId(): number | null {
      return this.account?.accountId;
   }

   get isManagedQueueStrategy(): boolean {
      const strategy = this.form.get('queueManagementStrategyTypeId')?.value as QueueManagementStrategyOption | null;

      return strategy?.queueManagementStrategyTypeId === QueueManagementStrategy.ManagedQueues;
   }

   @Input() get account(): GetAccountDetail_account | null {
      return this._account;
   }

   set account(value: GetAccountDetail_account | null) {
      this._account = value;
      if (
         value !== null &&
         value?.holdMusicPrompt?.promptId !== undefined &&
         value?.holdMusicPrompt?.promptId !== null
      ) {
         this.holdMusicOptionValue = 'holdMusicPromptId';
      } else {
         this.holdMusicOptionValue = 'default';
      }

      if (value && value.holdMusicPrompt) {
         this._originalHoldMusicPrompt = value;
      }

      if (value) {
         this._previousQueueStrategy = { queueManagementStrategyTypeId: value.queueManagementStrategyTypeId };

         this.formService.updateAccount(value);
      }

      if (
         this._account?.extensionsConnection.items !== undefined &&
         this._account?.extensionsConnection.items?.length > 0
      ) {
         this.form.get('operatorExtension')?.setValidators(Validators.required);
      }
   }

   @Input() sipDomain!: string;

   @Input() isAdminUser: string;

   @Input() set accountPrivileges(privileges: AccountPrivileges) {
      this._accountPrivileges = privileges;
      if (!privileges.canEdit) {
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
      }
   }

   get accountPrivileges(): AccountPrivileges {
      return this._accountPrivileges;
   }

   @Input() set extensionPrivileges(privileges: ExtensionPrivileges) {
      this._extensionPrivileges = privileges;
   }

   get extensionPrivileges(): ExtensionPrivileges {
      return this._extensionPrivileges;
   }

   private _extensionPrivileges: ExtensionPrivileges;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<AccountDetailSaveEvent>();

   @Output() resetMwi = new EventEmitter<any>();

   form: UntypedFormGroup;

   holdMusicOptionValue = 'default';

   promptsToInclude: number[] = [];

   hasLogLevelPrivilege: boolean;

   private _account: GetAccountDetail_account | null;

   private _accountPrivileges: AccountPrivileges;

   private _previousQueueStrategy: QueueManagementStrategyOption | null;

   constructor(
      private store: Store,
      public formService: AccountDetailFormService,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      protected changeDetectorRef: ChangeDetectorRef,
      private dialogService: LcsDialogService
   ) {
      super();
      this.form = this.formService.form;
   }

   ngOnInit(): void {
      this.actions$.pipe(ofType(createPromptSuccessNoRouteToDetail), takeUntil(this.destroyed$)).subscribe((action) => {
         this.promptsToInclude.push(action.promptItem.promptId);
         this.form.controls['holdMusicPrompt'].setValue(action.promptItem);
         this.form.controls['holdMusicPrompt'].markAsDirty();
         this.form.controls['holdMusicPrompt'].updateValueAndValidity();
      });
      this.store.select(getCurrentUserIsSystemUser).subscribe((isSystemUser) => {
         this.formService.toggleSystemFields(isSystemUser, this.accountPrivileges.canEdit);
      });

      this.store
         .select(getLogLevelPrivileges)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((logLevelPrivileges) => {
            this.hasLogLevelPrivilege = logLevelPrivileges.enabled;
            if (!logLevelPrivileges.enabled) {
               setTimeout(() => {
                  this.form.controls.logLevelId.disable();
               }, 0);
            }
         });

      this.form.get('isCallRecordingFeatureEnabled')?.valueChanges.subscribe((enabled: boolean) => {
         if (enabled && this.accountPrivileges.canEdit) {
            this.form.get('callRetentionPolicyTypeId')?.enable();
            this.form.get('isRecordInboundCalls')?.enable();
            this.form.get('isRecordOutboundCalls')?.enable();
            this.form.get('isRecordInternalCalls')?.enable();
            this.form.get('isOtrEnabled')?.enable();
         } else {
            this.form.get('callRetentionPolicyTypeId')?.disable();

            this.form.get('isRecordInboundCalls')?.setValue(false, { emitEvent: false });
            this.form.get('isRecordInboundCalls')?.disable({ emitEvent: false });

            this.form.get('isRecordOutboundCalls')?.setValue(false, { emitEvent: false });
            this.form.get('isRecordOutboundCalls')?.disable({ emitEvent: false });

            this.form.get('isRecordInternalCalls')?.setValue(false, { emitEvent: false });
            this.form.get('isRecordInternalCalls')?.disable({ emitEvent: false });

            this.form.get('isOtrEnabled')?.setValue(false, { emitEvent: false });
            this.form.get('isOtrEnabled')?.disable({ emitEvent: false });
         }
      });
   }

   onQueueStrategyOptionSelected(event: LcsSingleSelectSelectedEvent<QueueManagementStrategyOption>): void {
      const selected = event.option.value?.queueManagementStrategyTypeId;

      if (selected === this._previousQueueStrategy?.queueManagementStrategyTypeId) {
         return;
      }

      this.confirmationService
         .confirm({
            data: {
               dialogType: ConfirmationDialogType.YesNo,
               header: 'Changing Queue Management Strategy',
               message:
                  'Changing the Queue Management Strategy will immediately log out all members from all queues associated with this account. This should only be done after hours.  Proceed with caution.',
            },
            size: DialogSize.Medium,
         })
         .afterClosed()
         .subscribe((confirmed) => {
            if (!confirmed) {
               this.form
                  .get('queueManagementStrategyTypeId')
                  ?.setValue(this._previousQueueStrategy, { emitEvent: false });
               this.form.get('queueManagementStrategyTypeId')?.markAsPristine();
            } else {
               this._previousQueueStrategy = { queueManagementStrategyTypeId: selected ?? null };
               this.form.get('queueManagementStrategyTypeId')?.markAsDirty();
            }
         });
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['account'] && this.account) {
         if (
            changes['account'] &&
            this.account?.extensionsConnection?.items?.some(
               (extension) => extension.type === ExtensionTypes.FindMeFollowMe && !extension.isArchived
            )
         ) {
            this.form.get('isFmFmEnabled')?.disable();
         }
      }
      this.updateMOHDisabledState();
   }
   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   dispatchUploadMohFile(): void {
      this.store.dispatch(addMohPrompt());
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   onActivateClick(isSystemUser: boolean): void {
      if (isSystemUser) {
         this.activateInternationalDialingDialog()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((confirmed) => {
               if (confirmed) {
                  this.form.get('isInternationalDialingSuspended')?.setValue(false);
                  this.form.get('isInternationalDialingSuspended')?.markAsDirty();
                  this.onSubmit();
               }
            });
      } else {
         this.confirmationService.confirm({
            data: {
               dialogType: ConfirmationDialogType.Ok,
               header: 'International Dialing Suspended',
               message:
                  'International dialing has been suspended on your account. Please contact your LCS representative to restore this service.',
            },
            size: DialogSize.Medium,
         });
      }
   }

   activateInternationalDialingDialog(): Observable<boolean> {
      const message = 'Are you sure you want to re-activate International Dialing on this account?';
      const dialogRef = this.confirmationService.confirm({
         data: {
            dialogType: ConfirmationDialogType.YesNo,
            header: 'Re-Activate International Dialing?',
            message: message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onHoldMusicOptionChange(event: MatCheckboxChange) {
      this.holdMusicOptionValue = event.checked ? 'default' : 'holdMusicPromptId';

      this.updateMOHDisabledState();
      this.formService.form.get('holdMusicPrompt')?.markAsDirty();
   }

   onResetMwiClick(): void {
      if (this.account) {
         this.resetMwi.emit(this.account.accountId);
      }
   }

   private getFormSubmittedEvent(): AccountDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.account) {
         return null;
      }

      const account = this.formService.getValue(this.account.accountId);
      let accountFields: (keyof AccountInput)[] = [];
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'name');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'activeStartDate');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'activeEndDate');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isSuspended');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'lcsAccountNumber');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isResidential');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'callRetentionPolicyTypeId');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'nameDirectoryPreferenceId');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'timeZoneId');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isInternationalDialingEnabled');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isInternationalDialingSuspended');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isCallRecordingFeatureEnabled');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isRecordInternalCalls');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isRecordInboundCalls');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isRecordOutboundCalls');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isMailboxMessageTranscriptionEnabled');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'pstnLineLimit');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isOtrEnabled');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'defaultAreaCode');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'logLevelId');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'faxFileTypeId');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isMissedCallsShown');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'reasonLogLevelChanged');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isLcsInternal');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'queueManagementStrategyTypeId');
      if (this.isManagedQueueStrategy) {
         accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isAgentCanAssign');
      }
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'schedule', 'scheduleId');
      accountFields = this.formService.addFieldForDirtyControl(
         accountFields,
         'operatorExtension',
         'operatorExtensionId'
      );
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'isFmFmEnabled');
      if (accountFields.includes('logLevelId')) {
         accountFields.push('reasonLogLevelChanged');
         accountFields.push('logLevelChanged');
      }
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'note', 'note');

      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'holdMusicPrompt', 'holdMusicPromptId');

      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'presenceEnabled', 'presenceEnabled');
      accountFields = this.formService.addFieldForDirtyControl(accountFields, 'outboundRecordingNotificationEnabled');
      return { account, accountFields };
   }

   onBulkConfigureClick(): void {
      this.store.dispatch(bulkConfigureExtensions({ accountId: this.account?.accountId }));
   }

   onRebootAllPhonesClick(): void {
      this.store.dispatch(rebootAllPhones({ accountId: this.account?.accountId }));
   }

   onEditPresenceCredentialsClick(): void {
      this.store.dispatch(
         editPresenceCredentials({
            username: this.account?.presenceUser ?? '',
            presenceEnabled: this.account?.presenceEnabled ?? false,
         })
      );
   }

   onAdvancedOptionsClick(): void {
      const dialogRef = this.dialogService.openFormComponent(AdvancedAccountSettingsComponent, {
         closeOnNavigation: true,
         data: {
            account: this.account,
         },
         disableClose: false,
         panelClass: ['dialog-sm', 'dialog-container'],
      });

      dialogRef
         .afterClosed()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((data) => {
            if (data) {
               this.patchDialogValuesAndMarkAsDirty(data);
            }
         });
   }

   onChangeLogLevelClick(): void {
      const dialogRef = this.dialogService.openFormComponent(AccountLogLevelComponent, {
         closeOnNavigation: true,
         data: {
            account: this.account,
         },
         disableClose: false,
         panelClass: ['dialog-md', 'dialog-container'],
      });

      dialogRef
         .afterClosed()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((data) => {
            if (data) {
               this.patchDialogValuesAndMarkAsDirty(data);
            }
         });
   }

   onActiveDateChangeClick(): void {
      const dialogRef = this.dialogService.openFormComponent(AccountActiveDatesComponent, {
         closeOnNavigation: true,
         data: {
            account: this.account,
         },
         disableClose: false,
         panelClass: ['dialog-md', 'dialog-container'],
      });

      dialogRef
         .afterClosed()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((data) => {
            if (data) {
               this.patchDialogValuesAndMarkAsDirty(data);
            }
         });
   }

   private patchDialogValuesAndMarkAsDirty(data: any) {
      this.form.patchValue(data);

      Object.keys(data).forEach((key) => {
         const control = this.form.get(key);
         if (control) {
            control.markAsDirty();
         }
      });

      if (this._account) {
         const updatedAccount = { ...this._account, ...data };
         this._account = updatedAccount;
      }

      this.changeDetectorRef.markForCheck();
   }

   private updateMOHDisabledState() {
      if (this.holdMusicOptionValue === 'holdMusicPromptId') {
         if (!this.accountPrivileges.canEdit) this.form.get('holdMusicPrompt')?.disable();
         else this.form.get('holdMusicPrompt')?.enable();
         this.form.get('holdMusicPrompt')?.setValidators(Validators.required);
         this.form.get('holdMusicPrompt')?.updateValueAndValidity();
         if (this._originalHoldMusicPrompt) {
            this.formService.updateAccount(this._originalHoldMusicPrompt);
         }
      } else {
         this.form.get('holdMusicPrompt')?.disable();
         this.form.get('holdMusicPrompt')?.setValidators(null);
         this.form.get('holdMusicPrompt')?.updateValueAndValidity();
         this.form.get('holdMusicPrompt')?.setValue(null);
         this.holdMusicOptionValue = 'default';
      }
   }
}
